/**
 * HTTP statuses that mean "this token is no good".
 *
 * 401 is the usual auth failure. 422 is also treated as one because the
 * backend's Flask-JWT-Extended middleware answers an invalid or expired JWT
 * with 422 rather than 401.
 */
export const AUTH_ERROR_STATUSES = [401, 422];

/**
 * Thrown when a request cannot be sent because there is no usable access token:
 * the refresh token is missing, expired, rejected, or the refresh itself failed.
 *
 * Deliberately carries no `.response`, so react-query's shared retry policy
 * (see `src/services/api/provider.tsx`) neither retries it nor mistakes it for
 * a 401. When the session itself is what failed, `Auth` has already cleared it
 * and started the redirect to login before throwing this.
 */
export class MissingAuthTokenError extends Error {
    constructor() {
        super('Request aborted: no valid access token available');
        this.name = 'MissingAuthTokenError';
    }
}

/**
 * True for a ky `HTTPError` (or anything shaped like one) whose response status
 * means the credentials were rejected.
 *
 * Deliberately defensive: this runs in `catch` blocks where the thrown value
 * can be anything at all, including `null` or a plain object thrown by one of
 * our own ky hooks.
 */
export const isAuthError = (err: unknown): boolean => {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;

    return typeof status === 'number' && AUTH_ERROR_STATUSES.includes(status);
};
